import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import TransferTabs from '../components/TransferTabs';
import QuickActions from '../components/QuickActions';
import TransactionReceiptModal from '../components/TransactionReceiptModal';
import { 
  getUserData, 
  subscribeToAccountUpdates, 
  subscribeToTransactions,
  getRecentTransactions,
  processTransfer,
  updateAccountNumbers,
  getActiveAccounts
} from '../utils/firestore';
import { getMockTransactions, getAccountTheme } from '../utils/mockData';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountKey, setSelectedAccountKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTransactions, setAllTransactions] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const showMockRef = useRef(false);
  const accountsRef = useRef([]);

  // Active (non-disabled) accounts for member UI
  const activeAccounts = getActiveAccounts(accounts);

  // Format currency (USD)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format US account number for display (groups of 4)
  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = String(number).replace(/\D/g, '');
    if (!cleaned) return '';
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  // Copy account number to clipboard
  const handleCopyClick = async (accountNumber, accountType) => {
    try {
      await navigator.clipboard.writeText(accountNumber.replace(/\s/g, ''));
      setCopiedAccount(accountType);
      setTimeout(() => setCopiedAccount(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Prefer previously selected account; else Checking; else Investment; else first
  const getPrimaryAccount = (list, preferredType = null) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    if (preferredType) {
      const match = list.find((acc) => String(acc.type || '') === preferredType);
      if (match) return match;
    }
    return (
      list.find((acc) => acc.type === 'Current' || acc.type === 'Checking') ||
      list.find((acc) => String(acc.type || '').toLowerCase() === 'investment') ||
      list[0] ||
      null
    );
  };

  const accountKey = (account, index = 0) =>
    `${String(account?.type || 'Account')}:${String(account?.accountNumber || index)}`;

  useEffect(() => {
    const active = getActiveAccounts(accounts);
    if (!active.length) return;
    const stillExists = active.some((a, i) => accountKey(a, i) === selectedAccountKey);
    if (!selectedAccountKey || !stillExists) {
      const primary = getPrimaryAccount(active);
      const idx = active.findIndex((a) => a === primary);
      setSelectedAccountKey(accountKey(primary, idx >= 0 ? idx : 0));
    }
  }, [accounts, selectedAccountKey]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadInitialData = async () => {
      try {
        const data = await getUserData(currentUser.uid);
        
        // Update account numbers if needed
        const updatedAccounts = await updateAccountNumbers(currentUser.uid);
        if (updatedAccounts) {
          setAccounts(updatedAccounts);
        } else {
          setAccounts(data.accounts || []);
        }
        
        setUserData(data);
        const accountList = data.accounts || [];
        accountsRef.current = accountList;
        showMockRef.current = data.showMockTransactions === true;

        // Check if user is new (has registrationDate)
        setIsNewUser(!!data.registrationDate);

        // Load transactions once (respects showMockTransactions in getRecentTransactions)
        try {
          const initial = await getRecentTransactions(currentUser.uid);
          setAllTransactions(initial);
        } catch (txErr) {
          if (data.showMockTransactions === true) {
            setAllTransactions(
              [
                ...getMockTransactions(getActiveAccounts(accountList), {
                  existingTransactions: [],
                }),
              ].sort((a, b) => new Date(b.date) - new Date(a.date))
            );
          } else {
            setAllTransactions([]);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Subscribe to account updates
    const unsubscribeAccounts = subscribeToAccountUpdates(
      currentUser.uid,
      (updatedAccounts) => {
        accountsRef.current = updatedAccounts || [];
        setAccounts(updatedAccounts);
      }
    );

    // Subscribe to transactions (real-time). Merge with mock only if user has showMockTransactions on.
    const unsubscribeTransactions = subscribeToTransactions(
      currentUser.uid,
      (realTransactions) => {
        const list = showMockRef.current
          ? [
              ...realTransactions,
              ...getMockTransactions(getActiveAccounts(accountsRef.current), {
                existingTransactions: realTransactions,
              }),
            ]
          : realTransactions;
        const normalized = list
          .map((t) => ({ ...t, date: t.date instanceof Date ? t.date : new Date(t.date) }))
          .sort((a, b) => b.date.getTime() - a.date.getTime());
        setAllTransactions(normalized);
      }
    );

    return () => {
      unsubscribeAccounts();
      unsubscribeTransactions();
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    showMockRef.current = userData?.showMockTransactions === true;
  }, [userData?.showMockTransactions, accounts]);

  const handleTransfer = async (transferData) => {
    try {
      await processTransfer({
        ...transferData,
        userId: currentUser.uid
      });

      // Open proof of payment for the most recent matching transfer
      try {
        const latest = await getRecentTransactions(currentUser.uid);
        const ref = String(transferData?.reference || '').trim();
        const amt = Number(transferData?.amount);
        const match =
          latest.find((t) => {
            const desc = String(t?.description || '');
            const isTransfer = String(t?.category || '').toLowerCase() === 'transfer';
            const isDebit = String(t?.type || '').toLowerCase() === 'debit';
            const amountMatches = Number(t?.amount) === amt;
            const refMatches = ref ? desc.includes(ref) : true;
            return isTransfer && isDebit && amountMatches && refMatches;
          }) || latest[0];

        if (match) setReceiptTransaction(match);
      } catch (_) {
        // If we can't fetch the latest transaction immediately, let the user open it from Recent transactions.
      }
    } catch (error) {
      console.error('Transfer error:', error);
      setErrorMessage('An error occurred while processing your transfer. Please try again or contact support if the issue persists.');
      throw error;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const getTransactionIcon = (category, accountType) => {
    const kind = String(accountType || category || '').toLowerCase();
    if (kind.includes('invest') || kind.includes('return') || kind.includes('dividend')) return '📈';
    if (kind.includes('inherit') || kind.includes('estate') || kind.includes('trust')) return '🏛️';
    if (kind.includes('interest') || kind.includes('saving')) return '🌱';
    switch (category?.toLowerCase()) {
      case 'investment returns':
        return '📈';
      case 'transfer':
        return '↔️';
      case 'bill pay':
        return '💰';
      case 'deposit':
        return '📱';
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
          </div>
        </main>
      </div>
    );
  }

  const selectedIndex = Math.max(
    0,
    activeAccounts.findIndex((a, i) => accountKey(a, i) === selectedAccountKey)
  );
  const currentAccount =
    activeAccounts[selectedIndex] ||
    getPrimaryAccount(activeAccounts, selectedAccountKey?.split(':')[0]);
  const currentBalance = Number(currentAccount?.available ?? currentAccount?.balance ?? 0);
  const theme = getAccountTheme(currentAccount?.type);
  const isInvestment = theme.kind === 'investment';
  const routingNumber = String(
    currentAccount?.routingNumber || currentAccount?.sortCode || '021000021'
  ).replace(/\D/g, '');

  const selectedType = String(currentAccount?.type || '');
  const visibleTransactions = allTransactions.filter((t) => {
    if (!selectedType) return true;
    const txType = String(t.accountType || t.fromAccount || '').trim();
    if (!txType) {
      return theme.kind === 'checking' || theme.kind === 'business';
    }
    if (txType.toLowerCase() === selectedType.toLowerCase()) return true;
    // Match Investment / portfolio activity loosely
    if (theme.kind === 'investment') {
      const hay = `${txType} ${t.category || ''} ${t.description || ''}`.toLowerCase();
      return hay.includes('invest') || hay.includes('portfolio') || hay.includes('dividend');
    }
    return false;
  });

  const now = new Date();
  const monthCredits = visibleTransactions.filter((t) => {
    const d = new Date(t.date);
    return t.type === 'credit' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthDebits = visibleTransactions.filter((t) => {
    const d = new Date(t.date);
    return t.type === 'debit' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthIn = monthCredits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const monthOut = monthDebits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const pendingTotal = visibleTransactions
    .filter((t) => String(t.status || '').toLowerCase() === 'pending')
    .reduce((s, t) => s + Math.abs(t.amount), 0);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const inflowLabel = isInvestment ? 'returns' : 'in';
  const outflowLabel = isInvestment ? 'fees / out' : 'out';

  return (
    <div className="min-h-screen bg-mist/80 pt-20 sm:pt-24">
      <Navigation />

      {errorMessage && (
        <div className="sticky top-16 sm:top-20 z-40 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 shadow-sm">
            <p className="text-sm text-red-800 flex-1">{errorMessage}</p>
            <button
              type="button"
              onClick={() => setErrorMessage('')}
              className="shrink-0 p-1 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <section className="mb-5 sm:mb-6">
          <p className="text-oak-500 text-sm sm:text-base">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-oak-900 mt-0.5">
            {greeting()}, {userData?.name?.split(' ')[0] || 'User'}
          </h1>
        </section>

        {activeAccounts.length > 1 && (
          <section className="mb-4 sm:mb-5">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {activeAccounts.map((account, index) => {
                const key = accountKey(account, index);
                const active = key === selectedAccountKey;
                const chipTheme = getAccountTheme(account.type);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedAccountKey(key)}
                    className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                      active
                        ? chipTheme.chipActive
                        : 'bg-white text-oak-700 border-oak-200 hover:border-oak-300'
                    }`}
                  >
                    <span className="mr-1.5" aria-hidden>
                      {chipTheme.icon}
                    </span>
                    {account.type}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="mb-6 sm:mb-8">
          <div
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${theme.heroClass} p-5 sm:p-7 shadow-lg text-white`}
          >
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.14)_0%,transparent_55%)]"
              aria-hidden
            />
            {isInvestment ? (
              <div
                className="pointer-events-none absolute -right-10 top-0 h-40 w-40 rounded-full bg-claire-300/10 blur-2xl"
                aria-hidden
              />
            ) : null}

            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${theme.badgeClass}`}
                  >
                    <span className="mr-1.5" aria-hidden>
                      {theme.icon}
                    </span>
                    {theme.subtitle}
                  </span>
                  <span className="text-white/50">·</span>
                  <span className="text-sm text-white/85">{userData?.name || 'Account holder'}</span>
                </div>

                <div>
                  <p className="text-sm text-white/75">{theme.label}</p>
                  <p className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                    {formatCurrency(currentBalance)}
                  </p>
                  <p className="mt-2 text-sm text-white/70">{theme.accent}</p>
                  {pendingTotal > 0 && (
                    <p className="text-xs text-amber-200 mt-2">
                      {formatCurrency(pendingTotal)} pending
                    </p>
                  )}
                </div>

                {isInvestment ? (
                  <div className="grid grid-cols-2 gap-3 max-w-md sm:grid-cols-3">
                    <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">This month</p>
                      <p className="mt-1 text-sm font-semibold text-emerald-200">
                        +{formatCurrency(monthIn)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">Fees</p>
                      <p className="mt-1 text-sm font-semibold text-white">{formatCurrency(monthOut)}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 col-span-2 sm:col-span-1">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">Ledger</p>
                      <p className="mt-1 text-sm font-semibold text-white">
                        {formatCurrency(Number(currentAccount?.balance ?? currentBalance))}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-3 lg:text-right">
                <div className="flex flex-wrap gap-4 lg:justify-end">
                  <div className="flex items-center gap-2">
                    <span className="text-white/70 text-xs sm:text-sm font-medium">Account no.</span>
                    <span className="font-mono text-white text-sm sm:text-base font-medium">
                      {formatAccountNumber(currentAccount?.accountNumber)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopyClick(currentAccount?.accountNumber, 'current')}
                      className="p-1.5 rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-colors"
                      title="Copy account number"
                    >
                      {copiedAccount === 'current' ? (
                        <span className="text-emerald-300 text-sm">✓</span>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2m8 0h2a2 2 0 012 2v2m2 4v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/70 text-xs sm:text-sm font-medium">Routing</span>
                    <span className="font-mono text-white text-sm sm:text-base font-medium">
                      {routingNumber}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopyClick(routingNumber, 'routingNumber')}
                      className="p-1.5 rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-colors"
                      title="Copy routing number"
                    >
                      {copiedAccount === 'routingNumber' ? (
                        <span className="text-emerald-300 text-sm">✓</span>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2m8 0h2a2 2 0 012 2v2m2 4v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <a
                  href="#transfer-section"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-oak-900 shadow-sm hover:bg-claire-50 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  {isInvestment ? 'Move funds' : 'Transfer'}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="transfer-section" className="mb-8">
          <div className="rounded-2xl border border-oak-100 bg-white shadow-soft overflow-hidden">
            <div className="border-b border-oak-100 px-4 sm:px-6 py-3">
              <h2 className="text-base font-semibold text-oak-900">Pay & transfer</h2>
              <p className="text-sm text-oak-600">
                Between your accounts, to another US bank, or to another Claireoaks member
              </p>
            </div>
            <TransferTabs accounts={activeAccounts} onTransfer={handleTransfer} currentUser={currentUser} />
          </div>
        </section>

        <section className="mb-6 sm:mb-8">
          <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${theme.softClass}`}>
            <span className="text-sm text-oak-600">
              {now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              <span className="text-oak-400"> · {currentAccount?.type || 'Account'}</span>
            </span>
            <span className="text-oak-300">·</span>
            <span className="text-sm text-green-700 font-medium">
              +{formatCurrency(monthIn)} {inflowLabel}
            </span>
            <span className="text-oak-300">·</span>
            <span className="text-sm text-red-700 font-medium">
              −{formatCurrency(monthOut)} {outflowLabel}
            </span>
          </div>
        </section>

        <section id="quick-actions" className="mb-8">
          <div className="mb-3">
            <h2 className="font-display text-lg font-semibold text-oak-900">Quick actions</h2>
            <p className="text-sm text-oak-600">Bills, deposits, cards, and statements in one place</p>
          </div>
          <QuickActions accounts={activeAccounts} />
        </section>

        {visibleTransactions.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div>
                <h2 className="text-base font-semibold text-oak-900">
                  {isInvestment ? 'Portfolio activity' : 'Recent transactions'}
                </h2>
                <p className="text-xs text-oak-500 mt-0.5">{theme.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  document.getElementById('quick-actions')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="text-sm font-medium text-claire-700 hover:text-claire-800"
              >
                Statements
              </button>
            </div>
            <div className="rounded-2xl border border-oak-100 bg-white shadow-soft overflow-hidden divide-y divide-oak-50">
              {visibleTransactions.slice(0, 20).map((transaction, index) => (
                <button
                  key={transaction.id || index}
                  type="button"
                  onClick={() => setReceiptTransaction(transaction)}
                  className="w-full text-left px-4 py-3 sm:py-3.5 hover:bg-mist/70 transition-colors flex items-center justify-between gap-3 touch-manipulation focus:outline-none focus:ring-2 focus:ring-claire-500/20 focus:ring-inset"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-oak-50 flex items-center justify-center shrink-0 text-base">
                      {getTransactionIcon(transaction.category, transaction.accountType)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-oak-900 truncate">{transaction.description}</p>
                      <p className="text-xs text-oak-500">
                        {formatDate(transaction.date)}
                        {transaction.accountType ? (
                          <span className="text-oak-400"> · {transaction.accountType}</span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <p
                    className={`shrink-0 font-medium text-sm sm:text-base ${
                      transaction.type === 'credit' ? 'text-green-600' : 'text-oak-900'
                    }`}
                  >
                    {transaction.type === 'credit' ? '+' : '−'}
                    {formatCurrency(Math.abs(transaction.amount))}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {receiptTransaction && (
          <TransactionReceiptModal
            transaction={receiptTransaction}
            userData={userData}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onClose={() => setReceiptTransaction(null)}
          />
        )}
      </main>
    </div>
  );
} 